import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { useOverlayTrigger } from 'react-aria';
import { useForm } from 'react-hook-form';
import { useOverlayTriggerState } from 'react-stately';

import { ToastManager } from '../../../ToastManager';
import { defaultTokenExamples } from '../flow-arguments/tokens';

import { useI18n } from '../../../hooks/useI18nFormatters';

import { su, suPx } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';

import { AnimationRemain } from '../../../components/animation/AnimationRemain';
import { SolidButton } from '../../../components/buttons/SolidButton';
import { SelectControllable } from '../../../components/forms/select/SelectControllable';
import { TextField } from '../../../components/forms/TextField';
import { Overlay } from '../../../components/overlay/Overlay';

import { flowTest } from './useFlowTest';

export function FlowTestOverlayTrigger(props) {
  const triggerRef = useRef();
  const triggerState = useOverlayTriggerState({});
  const overlayTrigger = useOverlayTrigger({ type: 'menu' }, triggerState, triggerRef);

  return (
    <>
      {props.renderTrigger(
        triggerRef,
        {
          ...overlayTrigger.triggerProps,
        },
        triggerState,
      )}
      <AnimationRemain condition={triggerState.isOpen} delay={200}>
        {(animationRemainProps) => {
          // TODO
          // Use FlowTestForm.js here.

          return (
            <FlowTestOverlay
              targetRef={triggerRef}
              overlayTrigger={overlayTrigger}
              triggerState={triggerState}
              triggerTokensById={props.triggerTokensById}
              animationRemainProps={animationRemainProps}
            />
          );
        }}
      </AnimationRemain>
    </>
  );
}

function FlowTestOverlay(props) {
  const buttonRef = useRef();

  const { i18n } = useI18n();
  const { register, handleSubmit, control } = useForm();

  function onSubmit(data) {
    const tokens = Object.entries(data).reduce((accumulator, [tokenKey, tokenValue]) => {
      const token = props.triggerTokensById[tokenKey];

      switch (token.type) {
        case 'number':
          accumulator[tokenKey] = Number(tokenValue);
          break;
        case 'string':
          accumulator[tokenKey] = tokenValue;
          break;
        case 'boolean':
          accumulator[tokenKey] = tokenValue === 'true';
          break;
        default:
          break;
      }

      return accumulator;
    }, {});

    flowTest
      .testFlow({ tokens })
      .then(() => {
        ToastManager.add({
          icon: glyph.system.flow,
          message: i18n.messageFormatter('flow.startTest'),
        });
      })
      .catch(ToastManager.handleError);

    props.triggerState.close();
  }

  function handleStartPress() {
    handleSubmit(onSubmit)();
  }

  function triggerTokensByIdMapper([tokenKey, token]) {
    if (token.type === 'string' || token.type === 'number') {
      const type = token.type === 'string' ? 'text' : 'number';
      const defaultValue = token.example ?? defaultTokenExamples[token.type];

      return (
        <TextField
          key={tokenKey}
          orientation="vertical"
          name={tokenKey}
          type={type}
          label={token.title}
          defaultValue={defaultValue}
          register={register}
        />
      );
    }

    if (token.type === 'boolean') {
      const options = [
        {
          id: 'true',
          textValue: i18n.messageFormatter('common.true'),
        },
        {
          id: 'false',
          textValue: i18n.messageFormatter('common.false'),
        },
      ];

      return (
        <SelectControllable
          key={tokenKey}
          orientation="vertical"
          name={tokenKey}
          label={token.title}
          defaultValue="true"
          options={options}
          control={control}
        />
      );
    }

    return null;
  }

  useEffect(() => {
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);
  }, []);

  return (
    <Overlay
      targetRef={props.targetRef}
      overlayProps={{}}
      overlayTriggerState={props.triggerState}
      offset={suPx(1)}
      animationRemainProps={props.animationRemainProps}
      autoFocus={true}
      restoreFocus={true}
    >
      <S.Form
        onSubmit={(event) => {
          // single element form causes submit on enter key in a field
          event.preventDefault();
        }}
      >
        {Object.entries(props.triggerTokensById).map(triggerTokensByIdMapper)}

        <S.ButtonWrapper>
          <SolidButton
            ref={buttonRef}
            styleColor="green"
            styleFlat={true}
            styleWidth="full"
            onPress={handleStartPress}
          >
            <SolidButton.Text>Start</SolidButton.Text>
          </SolidButton>
        </S.ButtonWrapper>
      </S.Form>
    </Overlay>
  );
}

function S() {}
FlowTestOverlay.S = S;

S.Form = styled.form`
  position: relative;
  padding: ${su(2)};
  outline: 0;
  width: 320px;

  ${TextField.Root} {
    margin: 0 0 ${su(1)};
  }

  ${SelectControllable.S.Root} {
    margin: 0 0 ${su(1)};
  }
`;

S.ButtonWrapper = styled.div`
  display: flex;
  padding-top: ${su(1)};
`;
